// Three ways of combining the same underlying poll dataset into a single
// trajectory line. Each produces one output point per input poll, anchored
// at that poll's date (or its `chart_date` if the same-day separator
// has been applied), so the smoothed line can be drawn with the same x
// positions as the individual poll markers.
//
// All three routines treat the input as already filtered — the caller is
// expected to drop the EP anchor (pollster === '2024 EP Election (actual
// result)') before passing polls in, since the anchor isn't a survey.

import type { Poll, PollsterName } from '../data/surveys.types';

const MS_PER_DAY = 86_400_000;

export interface TrajectoryPoint {
  chart_date: string;
  lead: number;
}

type PollLike = Pick<Poll, 'date' | 'lead' | 'pollster'> & { chart_date?: string };

const xDate = (p: PollLike): string => p.chart_date ?? p.date;
const tOf = (s: string): number => new Date(s).getTime();

/**
 * For each poll's date, the arithmetic mean lead of all polls within
 * the previous `windowDays` (inclusive of the current poll). Treats
 * every poll equally regardless of size or recency.
 */
export function simpleRollingAverage(
  polls: PollLike[],
  windowDays = 60,
): TrajectoryPoint[] {
  return polls.map((p) => {
    const end = tOf(p.date);
    const start = end - windowDays * MS_PER_DAY;
    let sum = 0;
    let n = 0;
    for (const q of polls) {
      const t = tOf(q.date);
      if (t >= start && t <= end) {
        sum += q.lead;
        n++;
      }
    }
    return { chart_date: xDate(p), lead: n === 0 ? 0 : sum / n };
  });
}

/**
 * For each poll's date, the exponential-recency-weighted mean of all
 * earlier-or-equal polls. weight = exp(-ln 2 · daysAgo / halfLifeDays),
 * so the weight halves every `halfLifeDays`.
 */
export function recencyWeightedEWMA(
  polls: PollLike[],
  halfLifeDays = 45,
): TrajectoryPoint[] {
  const decay = Math.LN2 / halfLifeDays;
  return polls.map((p) => {
    const end = tOf(p.date);
    let num = 0;
    let den = 0;
    for (const q of polls) {
      const daysAgo = (end - tOf(q.date)) / MS_PER_DAY;
      if (daysAgo < 0) continue;
      const w = Math.exp(-decay * daysAgo);
      num += w * q.lead;
      den += w;
    }
    return { chart_date: xDate(p), lead: den > 0 ? num / den : 0 };
  });
}

/**
 * Same as `recencyWeightedEWMA`, but each poll's lead is first reduced
 * by `adj[poll.pollster]` (its house-effect calibration in pp) before
 * being weighted. Pollsters absent from `adj` get a zero adjustment.
 */
export function houseEffectAdjusted(
  polls: PollLike[],
  adj: Partial<Record<PollsterName, number>>,
  halfLifeDays = 45,
): TrajectoryPoint[] {
  const decay = Math.LN2 / halfLifeDays;
  return polls.map((p) => {
    const end = tOf(p.date);
    let num = 0;
    let den = 0;
    for (const q of polls) {
      const daysAgo = (end - tOf(q.date)) / MS_PER_DAY;
      if (daysAgo < 0) continue;
      const w = Math.exp(-decay * daysAgo);
      const adjusted = q.lead - (adj[q.pollster] ?? 0);
      num += w * adjusted;
      den += w;
    }
    return { chart_date: xDate(p), lead: den > 0 ? num / den : 0 };
  });
}

// ---- same-day separator --------------------------------------------
// When two pollsters publish on the same date, their dots and any
// connecting lines collide. This utility groups by date, sorts each
// group by pollster priority (MT first, then Esprimi, then Sagalytics),
// and bumps each subsequent same-date poll forward by 1 day on the
// chart only — the underlying `date` field is preserved for any
// time-window arithmetic done downstream.

const POLLSTER_PRIORITY: Record<PollsterName, number> = {
  MaltaToday: 1,
  'Esprimi (Times of Malta)': 2,
  Sagalytics: 3,
  '2024 EP Election (actual result)': 0,
};

export type PollOnChart = Poll & { chart_date: string };

export function separateSameDayPolls(polls: Poll[]): PollOnChart[] {
  const byDate = new Map<string, Poll[]>();
  for (const p of polls) {
    const list = byDate.get(p.date);
    if (list) list.push(p);
    else byDate.set(p.date, [p]);
  }

  const result: PollOnChart[] = [];
  for (const [date, list] of byDate) {
    list.sort(
      (a, b) =>
        (POLLSTER_PRIORITY[a.pollster] ?? 99) -
        (POLLSTER_PRIORITY[b.pollster] ?? 99),
    );
    list.forEach((p, i) => {
      const d = new Date(date);
      d.setUTCDate(d.getUTCDate() + i);
      const chart_date = d.toISOString().slice(0, 10);
      result.push({ ...p, chart_date });
    });
  }
  result.sort((a, b) => a.chart_date.localeCompare(b.chart_date));
  return result;
}
